
import React, { useState } from 'react';
import { useProducts } from '../products/hooks/useProducts';

interface ProductSearchProps {
  onProductSelect: (product: any) => void;
  customerId?: string;
}

export const ProductSearch: React.FC<ProductSearchProps> = ({
  onProductSelect,
  customerId,
}) => {
  const [filters, setFilters] = useState({
    category: '',
    metalType: '',
    priceRange: '',
    vendor: '',
    customization: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const { products, goldRate } = useProducts();

  const activeProducts =
    products?.filter((p) => p.status === 'active' && p.margin_fixed) || [];

  const filteredProducts = activeProducts.filter((product) => {
    const matchesSearch =
      product.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      !filters.category || product.category === filters.category;
    const matchesMetal =
      !filters.metalType || product.metal_type === filters.metalType;
    const matchesVendor =
      !filters.vendor ||
      product.vendor_name.toLowerCase().includes(filters.vendor.toLowerCase());
    const matchesCustomization =
      !filters.customization ||
      (filters.customization === 'yes' && product.customization_possible) ||
      (filters.customization === 'no' && !product.customization_possible);

    return (
      matchesSearch &&
      matchesCategory &&
      matchesMetal &&
      matchesVendor &&
      matchesCustomization
    );
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price':
        return a.making_charges - b.making_charges;
      case 'delivery':
        return a.delivery_days - b.delivery_days;
      case 'weight':
        return a.gross_weight - b.gross_weight;
      default:
        return a.model_name.localeCompare(b.model_name);
    }
  });

  const calculatePrice = (product: any) => {
    if (!goldRate) return 0;
    const goldValue =
      (goldRate.rate_24k * product.purity) / 24 * product.gross_weight;
    const stoneValue = product.stone_weight * (product.stone_rate || 0);
    return (
      goldValue + stoneValue + product.making_charges + (product.value_addition || 0)
    );
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="bg-white rounded-lg border p-4 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">Product Catalog</h2>
          <div className="text-sm text-gray-600">
            Gold Rate (24K): ₹{goldRate?.rate_24k || 0}/gram
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <select
            value={filters.category}
            onChange={(e) =>
              setFilters({ ...filters, category: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Categories</option>
            <option value="Ring">Ring</option>
            <option value="Necklace">Necklace</option>
            <option value="Earrings">Earrings</option>
            <option value="Bracelet">Bracelet</option>
            <option value="Pendant">Pendant</option>
            <option value="Chain">Chain</option>
          </select>

          <select
            value={filters.metalType}
            onChange={(e) =>
              setFilters({ ...filters, metalType: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Metals</option>
            <option value="Gold">Gold</option>
            <option value="Silver">Silver</option>
            <option value="Platinum">Platinum</option>
          </select>

          <select
            value={filters.customization}
            onChange={(e) =>
              setFilters({ ...filters, customization: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Customization</option>
            <option value="yes">Available</option>
            <option value="no">Not Available</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="name">Sort by Name</option>
            <option value="price">Sort by Price</option>
            <option value="delivery">Sort by Delivery</option>
            <option value="weight">Sort by Weight</option>
          </select>

          <button
            onClick={() => {
              setFilters({
                category: '',
                metalType: '',
                priceRange: '',
                vendor: '',
                customization: '',
              });
              setSearchTerm('');
            }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedProducts.map((product) => (
          <div key={product.id} className="bg-white rounded-lg border hover:shadow-lg transition-shadow">
            <div className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{product.model_name}</h3>
                  <p className="text-sm text-gray-600">{product.vendor_name}</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-primary">
                    ₹{calculatePrice(product).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500">{product.delivery_days} days</div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span>{product.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Metal:</span>
                  <span>
                    {product.metal_type} ({product.purity}K)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Weight:</span>
                  <span>{product.gross_weight}g</span>
                </div>
                {product.stone_weight > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stones:</span>
                    <span>{product.stone_weight}ct</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Making:</span>
                  <span>₹{product.making_charges}</span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-gray-700 mb-3">{product.description}</p>

                {product.customization_possible && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                      <i className="ri-settings-line mr-1"></i>
                      Customizable
                    </span>
                  </div>
                )}

                <button
                  onClick={() => onProductSelect(product)}
                  className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line mr-1"></i>
                  Add to Order
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedProducts.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-600">
            Try adjusting your search criteria or filters
          </p>
        </div>
      )}
    </div>
  );
};
